/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useFeatureCodes.ts
 🗓️ Created: 2025-01-22
 🧩 Description:
    Custom hook for managing Feature Codes data and categories.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchFeatureCodes } from '../api/featureCodesApi';
import { fetchFeatureCategories } from '../api/featureCategoriesApi';
import type { FeatureCode, FeatureCategory } from '../types';

interface UseFeatureCodesOptions {
  tenantSlug?: string;
  userId?: string;
  domainId?: number;
  domainName?: string;
}

export function useFeatureCodes({ tenantSlug, userId, domainId, domainName }: UseFeatureCodesOptions) {
  const [categories, setCategories] = useState<FeatureCategory[]>([]);
  const [featureCodes, setFeatureCodes] = useState<FeatureCode[]>([]);
  const [originalValues, setOriginalValues] = useState<
    Map<string, { default_number: string; custom_number: string | null }>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const data = await fetchFeatureCategories(tenantSlug, userId, domainId, domainName);
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, [tenantSlug, userId, domainId, domainName]);

  // Load feature codes
  const loadFeatureCodes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchFeatureCodes({ domainId, domainName }, tenantSlug, userId);
      setFeatureCodes(data);

      // Store original values
      const originals = new Map<string, { default_number: string; custom_number: string | null }>();
      data.forEach((fc: FeatureCode) => {
        originals.set(fc.uuid || fc.id.toString(), {
          default_number: fc.default_number,
          custom_number: fc.custom_number,
        });
      });
      setOriginalValues(originals);
    } catch (error) {
      console.error('Error loading feature codes:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, userId, domainId, domainName]);

  useEffect(() => {
    // Load when tenantSlug + userId are available AND we have at least one domain identifier
    if (tenantSlug && userId && (domainName || (domainId && !isNaN(domainId)))) {
      loadCategories();
      loadFeatureCodes();
    }
  }, [tenantSlug, userId, domainId, domainName, loadCategories, loadFeatureCodes]);

  // Get feature codes by category
  const getFeatureCodesByCategory = useCallback(
    (categoryId: number): FeatureCode[] => {
      return featureCodes
        .filter((fc) => fc.category_id === categoryId)
        .sort((a, b) => a.default_number.localeCompare(b.default_number));
    },
    [featureCodes]
  );

  // Filter feature codes based on search query
  const filterFeatureCodes = useCallback(
    (codes: FeatureCode[]): FeatureCode[] => {
      if (!searchQuery.trim()) {
        return codes;
      }

      const query = searchQuery.toLowerCase();
      return codes.filter((code) => {
        const name = code.name?.toLowerCase() || '';
        const description = code.description?.toLowerCase() || '';
        const defaultNumber = code.default_number?.toLowerCase() || '';
        const customNumber = code.custom_number?.toLowerCase() || '';

        return (
          name.includes(query) ||
          description.includes(query) ||
          defaultNumber.includes(query) ||
          customNumber.includes(query)
        );
      });
    },
    [searchQuery]
  );

  // Get filtered categories (only show categories that have matching feature codes)
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return categories;
    }

    return categories.filter((category) => {
      const categoryCodes = getFeatureCodesByCategory(category.id);
      const filteredCodes = filterFeatureCodes(categoryCodes);
      return filteredCodes.length > 0;
    });
  }, [categories, searchQuery, getFeatureCodesByCategory, filterFeatureCodes]);

  return {
    categories,
    featureCodes,
    originalValues,
    loading,
    searchQuery,
    setSearchQuery,
    filteredCategories,
    getFeatureCodesByCategory,
    filterFeatureCodes,
    refresh: loadFeatureCodes,
  };
}

